<div class="w-full max-w-7xl items-center lg:grid lg:grid-cols-2 lg:gap-10">
  <!-- Left section -->
  <section class="mb-8 text-white lg:mb-0" aria-label="About Comcast Business">
    <h1 class="font-montserrat text-3xl font-light lg:text-5xl lg:leading-14">
      Nitel is now part of
      <br />
      Comcast Business
    </h1>

    <p
      class="font-lato mt-4 max-w-xl text-sm font-normal text-white lg:text-base"
    >
      Our broad portfolio accommodates a range of needs — from turnkey to
      complex solution suites — for businesses of all sizes.
    </p>

    <a
      href="https://business.comcast.com/enterprise"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Learn more about Comcast Business (opens in a new tab)"
      class="tertiary-button"
    >
      LEARN MORE
    </a>
  </section>

  <!-- Right login card -->
  <section class="flex lg:justify-end" aria-label="Sign in">
    <div
      class="w-full max-w-xl rounded-xl bg-white p-8 text-black shadow-lg md:p-10"
    >
      <!-- Comcast Business | Nitel Logo -->
      <div class="mb-4 flex md:justify-center">
        <a
          routerLink="/"
          class="inline-block"
          aria-label="Comcast Business — go to homepage"
        >
          <img
            src="assets/images/logos/CB_Logo_Color_RGB.svg"
            alt=""
            role="presentation"
            class="h-10 w-auto md:h-14"
          />
        </a>
      </div>

      <div class="mx-auto mt-4 flex flex-col items-center justify-center">
        <app-login-status-banner></app-login-status-banner>
      </div>

      <!-- Title -->
      <h2 class="comcast-h2">Sign in</h2>

      <!-- Step 1: Username Input -->
      @if (currentStep() === 'username') {
        <form
          [formGroup]="usernameFormGroup"
          (ngSubmit)="checkLoginProvider()"
          class="mt-4"
          novalidate
        >
          <label for="usernameId" class="comcast-input-label">Username</label>
          <input
            formControlName="username"
            id="usernameId"
            type="text"
            required
            name="username"
            autocomplete="username"
            aria-describedby="step1-username-error"
            [attr.aria-invalid]="messages().usernameInput.length > 0"
            [ngClass]="{
              'comcast-input-invalid': messages().usernameInput.length > 0,
            }"
            class="comcast-input"
            placeholder="Enter your username"
          />
          <div aria-live="polite">
            @if (messages().usernameInput) {
              <span id="step1-username-error" class="comcast-validation-error">
                {{ messages().usernameInput }}
              </span>
            }
          </div>

          <div class="mt-6 flex">
            <button
              type="submit"
              class="btn-primary"
              [attr.aria-busy]="isLoading()"
            >
              @if (isLoading()) {
                <span class="sr-only">Loading, please wait</span>
                <div class="flex min-w-12 justify-center" aria-hidden="true">
                  <mat-spinner [diameter]="20"></mat-spinner>
                </div>
              } @else {
                Next
                <mat-icon
                  class="flex h-4.5! align-middle! text-lg! leading-none!"
                  >arrow_forward</mat-icon
                >
              }
            </button>
          </div>

          <div class="font-lato mt-6 flex text-sm">
            New to MyPortal?&nbsp;
            <a
              routerLink="/register"
              class="font-lato text-sm font-bold hover:underline"
              >Register for MyPortal</a
            >
          </div>
        </form>
      } @else if (currentStep() === 'password') {
        <!-- Step 2: Password Input -->
        <form
          [formGroup]="loginFormGroup"
          class="mt-4"
          novalidate
          (ngSubmit)="login()"
        >
          <label for="usernameId" class="comcast-input-label">Username</label>

          <input
            formControlName="username"
            id="usernameId"
            type="text"
            required
            name="username"
            autocomplete="username"
            aria-describedby="step2-username-error"
            [attr.aria-invalid]="messages().usernameInput.length > 0"
            [ngClass]="{
              'comcast-input-invalid': messages().usernameInput.length > 0,
            }"
            class="comcast-input"
            placeholder="Enter your username"
          />
          <div aria-live="polite">
            @if (messages().usernameInput) {
              <span id="step2-username-error" class="comcast-validation-error">
                {{ messages().usernameInput }}
              </span>
            }
          </div>

          <label for="passwordId" class="comcast-input-label mt-4"
            >Password</label
          >
          <input
            formControlName="password"
            id="passwordId"
            type="password"
            placeholder="Enter your password"
            required
            name="password"
            autocomplete="current-password"
            aria-describedby="password-error"
            [attr.aria-invalid]="messages().passwordInput.length > 0"
            [ngClass]="{
              'comcast-input-invalid': messages().passwordInput.length > 0,
            }"
            class="comcast-input"
          />
          <div aria-live="polite">
            @if (messages().passwordInput) {
              <span id="password-error" class="comcast-validation-error">
                {{ messages().passwordInput }}
              </span>
            }
          </div>

          <div class="mt-4 mb-8 flex justify-end md:mt-1 md:mb-0">
            <a
              [routerLink]="'/forgot-password'"
              [queryParams]="{
                username: $safeNavigationMigration(
                  loginFormGroup.get('username')?.value
                ),
              }"
              class="comcast-blue-link font-bold"
            >
              Forgot your password?
            </a>
          </div>

          @if (errorMessage()) {
            <div class="comcast-validation-error" role="alert">
              {{ errorMessage() }}
            </div>
          }

          <div class="mt-2 flex gap-4">
            <button
              (click)="goBack()"
              type="button"
              class="btn-secondary"
              [disabled]="isLoading()"
            >
              <mat-icon class="flex h-4.5! align-middle! text-lg! leading-none!"
                >arrow_back</mat-icon
              >
              Back
            </button>
            <button
              type="submit"
              class="btn-primary"
              [attr.aria-busy]="isLoading()"
            >
              @if (isLoading()) {
                <span class="sr-only">Loading, please wait</span>
                <div class="flex min-w-12 justify-center" aria-hidden="true">
                  <mat-spinner [diameter]="20"></mat-spinner>
                </div>
              } @else {
                Login
              }
            </button>
          </div>

          <div class="mx-auto mt-8 flex items-center justify-center md:mt-4">
            <app-toggle
              formControlName="toggle"
              label="Keep me logged in"
              size="md"
              color="primary"
            >
            </app-toggle>
          </div>
        </form>
      }
    </div>
  </section>
</div>
